import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardList, FileText, Calendar, GitBranch, Pencil, HelpCircle, ChevronDown } from 'lucide-react';
import { surveyAPI, questionsAPI } from 'services/swagger-api/surveyApi';
import { SurveyDetailDto, QuestionDto, PossibleAnswerDto } from 'api/survey';
import { GenericLoading } from 'components/ui/Loader';
import { Button } from 'components/ui/Button';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'enums/sizes';
import { InputText } from 'components/forms/InputText';
import { EntityDetailsHeader, EntityDetailsHeaderConfig } from 'components/display/EntityDetailsHeader';
import clsx from 'clsx';

export function SurveyDetails() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { openDialog, closeDialog, setDialogActions } = useDialog();

  const [survey, setSurvey] = useState<SurveyDetailDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  // Utiliser des refs pour éviter les problèmes de closure
  const editingQuestionLabelRef = useRef('');
  const editingAnswerLabelRef = useRef('');

  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const loadSurvey = useCallback(async () => {
    if (!surveyId) return;
    try {
      setIsLoading(true);
      const response = await surveyAPI.getSurvey({ id: surveyId, includeQuestions: true });
      setSurvey(response.survey || null);
    } catch (error) {
      console.error('Error loading survey:', error);
    } finally {
      setIsLoading(false);
    }
  }, [surveyId]);

  useEffect(() => {
    loadSurvey();
  }, [loadSurvey]);

  const handleEditQuestionLabel = (question: QuestionDto) => {
    editingQuestionLabelRef.current = question.label || '';
    openDialog({
      title: "Modifier le label de la question",
      size: Sizes["2xl"]
    }, <EditQuestionLabelContent
      initialLabel={question.label || ''}
      onLabelChange={(label) => { editingQuestionLabelRef.current = label; }}
    />);
    setDialogActions({
      onSubmit: async () => {
        try {
          await questionsAPI.updateQuestion({
            id: question.id,
            updateQuestionCommand: {
              questionTypeId: question.questionTypeId,
              key: question.key,
              label: editingQuestionLabelRef.current,
              displayOrder: question.displayOrder,
              step: question.step,
              iconKey: question.iconKey,
              groupTitle: question.groupTitle,
              isRequired: question.isRequired,
              dependsOn: question.dependsOn,
              constraints: question.constraints,
              skipIf: question.skipIf
            }
          });
          closeDialog();
          loadSurvey();
        } catch (error) {
          console.error('Error updating question label:', error);
        }
      },
      submitLabel: "Enregistrer",
      closeLabel: "Annuler"
    });
  };

  const handleEditAnswerLabel = (question: QuestionDto, answer: PossibleAnswerDto) => {
    editingAnswerLabelRef.current = answer.label || '';
    openDialog({
      title: "Modifier le label de la réponse",
      size: Sizes["2xl"]
    }, <EditAnswerLabelContent
      initialLabel={answer.label || ''}
      onLabelChange={(label) => { editingAnswerLabelRef.current = label; }}
    />);
    setDialogActions({
      onSubmit: async () => {
        try {
          await questionsAPI.updatePossibleAnswer({
            questionId: question.id,
            answerId: answer.id!,
            updatePossibleAnswerCommand: {
              key: answer.key || '',
              label: editingAnswerLabelRef.current,
              valueType: answer.valueType || 'string',
              displayOrder: answer.displayOrder || 1,
              iconKey: answer.iconKey,
              hasChildren: answer.hasChildren,
              valueData: answer.valueData
            }
          });
          closeDialog();
          loadSurvey();
        } catch (error) {
          console.error('Error updating answer label:', error);
        }
      },
      submitLabel: "Enregistrer",
      closeLabel: "Annuler"
    });
  };

  if (isLoading || !surveyId) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <GenericLoading loading />
        </div>
      </section>
    );
  }

  if (!survey) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-red-500">Questionnaire introuvable</p>
        </div>
      </section>
    );
  }

  const headerConfig: EntityDetailsHeaderConfig = {
    icon: <ClipboardList className="text-primary" size={28} />,
    title: survey.name || 'Questionnaire',
    subtitle: survey.description || `Version ${survey.version || 1}`,
    infoSections: [
      {
        icon: FileText,
        label: "CONTEXTE",
        value: survey.context?.label || survey.contextId || '-',
        disabled: true
      },
      ...(survey.branchName ? [{
        icon: GitBranch,
        label: "BRANCHE",
        value: survey.branchName,
        disabled: true
      }] : []),
      {
        icon: HelpCircle,
        label: "QUESTIONS",
        value: (survey.questions?.length || 0).toString(),
        disabled: true
      }
    ],
    badge: {
      label: survey.isActive ? 'Actif' : 'Inactif',
      variant: survey.isActive ? 'green' : 'gray',
      icon: ClipboardList
    },
    metadata: survey.creationTime ? {
      icon: Calendar,
      text: `Créé le ${new Date(survey.creationTime).toLocaleDateString('fr-FR')}`
    } : undefined
  };

  return (
    <section className="w-full overflow-hidden border bg-white rounded-2xl">
      <EntityDetailsHeader config={headerConfig} />
      <div className="p-6 shadow-inner">
        {/* Questions Accordion */}
        {survey.questions && survey.questions.length > 0 ? (
          <div className="space-y-2">
            {survey.questions.map((question: QuestionDto, index: number) => {
              const isExpanded = expandedQuestions.has(question.id);
              const hasAnswers = question.possibleAnswers && question.possibleAnswers.length > 0;

              return (
                <div
                  key={question.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  {/* Accordion Header */}
                  <div
                    className={clsx(
                      "flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors",
                      isExpanded && hasAnswers && "border-b border-gray-100"
                    )}
                    onClick={() => hasAnswers && toggleQuestion(question.id)}
                  >
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </span>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{question.label}</p>
                        <Button
                          plain
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleEditQuestionLabel(question);
                          }}
                          className="text-gray-400 hover:text-primary"
                        >
                          <Pencil size={14} />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                          {question.questionTypeLabel || question.questionTypeCode}
                        </span>
                        {question.isRequired && (
                          <span className="text-red-500 font-medium">Requis</span>
                        )}
                        {hasAnswers && (
                          <span className="text-gray-400">
                            {question.possibleAnswers!.length} réponse{question.possibleAnswers!.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {hasAnswers && (
                      <ChevronDown
                        className={clsx(
                          "w-5 h-5 text-gray-400 transition-transform",
                          isExpanded && "rotate-180"
                        )}
                      />
                    )}
                  </div>

                  {/* Accordion Content - Possible Answers */}
                  {hasAnswers && isExpanded && (
                    <div className="px-4 py-3 bg-gray-50">
                      <div className="space-y-2">
                        {question.possibleAnswers!.map((answer: PossibleAnswerDto) => (
                          <div
                            key={answer.id}
                            className="flex items-center gap-3 px-3 py-2 bg-white rounded-md border border-gray-100"
                          >
                            <span className="w-2 h-2 rounded-full bg-primary/40 flex-shrink-0" />
                            <span className="text-sm text-gray-700 flex-1">{answer.label}</span>
                            <Button
                              plain
                              onClick={() => handleEditAnswerLabel(question, answer)}
                              className="text-gray-400 hover:text-primary"
                            >
                              <Pencil size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-12 text-center text-gray-500">
            <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune question dans ce questionnaire</p>
          </div>
        )}
      </div>
    </section>
  );
}

// Composant séparé pour le contenu du dialog d'édition de label question
function EditQuestionLabelContent({
  initialLabel,
  onLabelChange
}: {
  initialLabel: string;
  onLabelChange: (label: string) => void;
}) {
  const [label, setLabel] = useState(initialLabel);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
    onLabelChange(e.target.value);
  };

  return (
    <div className="py-2">
      <InputText
        label="Label de la question"
        value={label}
        onChange={handleChange}
        placeholder="Entrez le nouveau label..."
      />
    </div>
  );
}

// Composant séparé pour le contenu du dialog d'édition de label réponse
function EditAnswerLabelContent({
  initialLabel,
  onLabelChange
}: {
  initialLabel: string;
  onLabelChange: (label: string) => void;
}) {
  const [label, setLabel] = useState(initialLabel);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
    onLabelChange(e.target.value);
  };

  return (
    <div className="py-2">
      <InputText
        label="Label de la réponse"
        value={label}
        onChange={handleChange}
        placeholder="Entrez le nouveau label..."
      />
    </div>
  );
}
